import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Users, Grid3x3, Crown, Pencil, LogOut, MessageCircle, Trash2, LayoutDashboard, Mail } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, mediaSrc, fmtCount, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";
import SubscribeButton from "@/components/SubscribeButton";
import PostTile from "@/components/PostTile";
import EditProfileDialog from "@/components/EditProfileDialog";
import TipButton from "@/components/TipDialog";
import { LockedMedia, useLike } from "@/components/FeedCard";
import CommentsSheet from "@/components/CommentsSheet";

function PostViewer({ post: initial, onClose, isMe, onDeleted }) {
  const [post, , like] = useLike(initial);
  const [comments, setComments] = useState(false);
  if (!post) return null;
  const del = async () => {
    try { await api.delete(`/posts/${post.id}`); toast.success("Post deleted"); onDeleted(post.id); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Dialog open={!!initial} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm overflow-hidden rounded-md border-[#2A2E37] bg-[#121418] p-0 text-white" data-testid="post-viewer">
        <DialogTitle className="sr-only">Post</DialogTitle>
        <div className="relative aspect-[9/16] max-h-[75vh] w-full">
          {post.locked ? <LockedMedia post={post} /> : post.media_type === "video" ? (
            <video src={mediaSrc(post.media_src)} poster={mediaSrc(post.cover_src) || undefined} autoPlay loop playsInline controls className="h-full w-full object-cover" data-testid="viewer-video" />
          ) : <img src={mediaSrc(post.media_src)} alt={post.caption} className="h-full w-full object-cover" />}
        </div>
        <div className="flex items-center justify-between gap-3 px-4 pb-4">
          <p className="text-sm text-gray-200">{post.caption}</p>
          <div className="flex items-center gap-3">
            <button onClick={like} data-testid="viewer-like-btn" className="flex items-center gap-1 text-sm">
              <Heart className={`h-5 w-5 ${post.liked ? "fill-[#FF2E4D] text-[#FF2E4D]" : ""}`} />{fmtCount(post.likes_count)}
            </button>
            <button onClick={() => setComments(true)} data-testid="viewer-comment-btn"><MessageCircle className="h-5 w-5" /></button>
            {isMe && <button onClick={del} data-testid="viewer-delete-btn" className="text-gray-400 hover:text-red-400"><Trash2 className="h-5 w-5" /></button>}
          </div>
        </div>
        <CommentsSheet post={post} open={comments} onOpenChange={setComments} />
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon: Icon, value, label, id }) {
  return (
    <div className="flex items-center gap-2" data-testid={id}>
      <Icon className="h-3.5 w-3.5 text-gray-500" strokeWidth={1.75} />
      <span className="text-sm font-semibold tabular-nums">{fmtCount(value)}</span>
      <span className="text-xs text-gray-400">{label}</span>
    </div>
  );
}

export default function Profile() {
  const { username } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(null);
  const [editing, setEditing] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, ps] = await Promise.all([api.get(`/users/${username}`), api.get(`/users/${username}/posts`)]);
      setProfile(p.data);
      setPosts(ps.data);
    } catch { setNotFound(true); }
  }, [username]);

  useEffect(() => { load(); }, [load, user?.id]);

  if (notFound) return <div className="p-16 text-center text-gray-400" data-testid="profile-not-found">User not found.</div>;
  if (!profile) return <div className="p-16 text-center text-gray-500">Loading...</div>;

  const shown = tab === "premium" ? posts.filter((p) => p.is_premium) : posts;

  return (
    <div className="pb-16" data-testid="profile-page">
      <div className="relative h-40 overflow-hidden bg-[#1C1F26] md:h-56">
        {profile.banner_url && <img src={mediaSrc(profile.banner_url)} alt="" className="h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#090A0C] via-[#090A0C]/30 to-transparent" />
      </div>
      <div className="mx-auto max-w-6xl px-4 md:px-8">
        <div className="relative -mt-10 flex items-end gap-4 md:-mt-12 md:gap-5">
          <Avatar user={profile} size="h-20 w-20 md:h-24 md:w-24" className="ring-4 ring-[#090A0C]" />
          <div className="min-w-0 pb-1">
            <h1 className="truncate text-xl font-semibold tracking-tight sm:text-2xl" data-testid="profile-name">{profile.name}</h1>
            <p className="text-sm text-gray-400" data-testid="profile-username">@{profile.username}</p>
          </div>
          {profile.is_me ? (
            <div className="ml-auto flex items-center gap-2 pb-1">
              {profile.is_creator && (
                <Link to="/dashboard" data-testid="profile-dashboard-btn"
                  className="flex items-center gap-2 rounded-sm border border-[#2A2E37] bg-white/5 px-3.5 py-2 text-xs font-medium transition-colors hover:bg-white/10">
                  <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
                </Link>
              )}
              <button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
                className="flex items-center gap-2 rounded-sm border border-[#2A2E37] bg-white/5 px-3.5 py-2 text-xs font-medium transition-colors hover:bg-white/10">
                <Pencil className="h-3.5 w-3.5" /> Edit profile
              </button>
              <button onClick={async () => { await logout(); nav("/"); }} data-testid="profile-logout-btn" aria-label="Log out"
                className="rounded-sm border border-[#2A2E37] bg-white/5 p-2 transition-colors hover:bg-white/10"><LogOut className="h-3.5 w-3.5" /></button>
            </div>
          ) : !profile.is_bot && (
            <Link to={user ? `/messages/${profile.username}` : "/login"} data-testid="profile-message-btn"
              className="ml-auto mb-1 flex items-center gap-2 rounded-sm border border-[#2A2E37] bg-white/5 px-3.5 py-2 text-xs font-medium transition-colors hover:bg-white/10">
              <Mail className="h-3.5 w-3.5" strokeWidth={1.75} /> Message
            </Link>
          )}
        </div>
        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-12">
          {profile.is_creator && (
            <aside className="h-fit rounded-md border border-[#2A2E37] bg-[#121418] p-5 lg:sticky lg:top-20 lg:order-last" data-testid={profile.is_me ? "own-price-box" : "subscribe-box"}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">{profile.is_me ? "Your membership" : "Membership"}</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums" data-testid={profile.is_me ? "own-price" : "profile-price"}>
                ${profile.price}<span className="ml-1 text-sm font-normal text-gray-500">/ month</span>
              </p>
              <p className="mb-4 mt-1 text-xs leading-relaxed text-gray-400">
                {profile.is_me ? "Change your price anytime from Edit profile." : "Unlock every subscriber-only post. Cancel anytime."}
              </p>
              {!profile.is_me && (
                <div className="space-y-2">
                  <SubscribeButton creator={profile} className="w-full" />
                  <TipButton creator={profile} className="w-full" />
                </div>
              )}
            </aside>
          )}
          <div className="min-w-0">
            {profile.bio && <p className="max-w-2xl text-sm leading-relaxed text-gray-300" data-testid="profile-bio">{profile.bio}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-5">
              {profile.is_creator && <Stat icon={Users} value={profile.subscribers_count} label="subscribers" id="stat-subscribers" />}
              <Stat icon={Heart} value={profile.likes_count} label="likes" id="stat-likes" />
              <Stat icon={Grid3x3} value={profile.posts_count} label="posts" id="stat-posts" />
            </div>
            <Link to={`/c?about=${profile.username}`} data-testid="profile-discussions-link" className="mt-3 inline-block text-xs text-gray-400 underline-offset-4 hover:text-white hover:underline">
              Community discussions about @{profile.username} &rarr;
            </Link>
            {profile.is_creator && <Tabs value={tab} onValueChange={setTab} className="mt-8">
              <TabsList className="h-9 rounded-sm border border-[#2A2E37] bg-[#121418] p-1">
                <TabsTrigger value="all" className="rounded-sm px-4 text-xs data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="tab-all-posts">All posts</TabsTrigger>
                <TabsTrigger value="premium" className="rounded-sm px-4 text-xs data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="tab-premium-posts">
                  <Crown className="mr-1.5 h-3 w-3" /> Subscribers only
                </TabsTrigger>
              </TabsList>
            </Tabs>}
            <div className={`${profile.is_creator ? "mt-4" : "mt-8"} grid grid-cols-3 gap-1.5 md:grid-cols-4 md:gap-3`} data-testid="profile-posts-grid">
              {shown.map((p, i) => <PostTile key={p.id} post={p} index={i} onOpen={setOpen} />)}
            </div>
            {shown.length === 0 && (
              <p className="mt-10 text-center text-sm text-gray-500" data-testid="profile-no-posts">
                No posts yet. {profile.is_me && <Link to="/upload" className="text-[#FF2E4D] hover:underline">Post your first one</Link>}
              </p>
            )}
          </div>
        </div>
      </div>
      <PostViewer post={open} isMe={profile.is_me} onClose={() => setOpen(null)}
        onDeleted={(id) => { setOpen(null); setPosts((ps) => ps.filter((p) => p.id !== id)); }} />
      {profile.is_me && <EditProfileDialog open={editing} onOpenChange={setEditing} onSaved={load} />}
    </div>
  );
}
